/**
 * Builds a plain node shaped like a secondary-view `subIssues.nodes[]` entry so
 * `SubIssuesListItem` can render an add/create while the write is in flight. Unknown fields get
 * neutral placeholders; the settle (folded server node or refetch) replaces the node moments later.
 */
import {md2htmlInline} from '@github-ui/md2html'
import type {SubIssuesListItemNode} from '../components/sub-issues-list-item-query-types'

// `state` / `stateReason` reach these builders from Relay-typed sources (the issue picker, the
// mutation payload) whose enums are open: the server may send a value this client doesn't know yet.
// They're taken as strings and narrowed to the row's closed unions below, so an unrecognised value
// can't be passed off as a known one.
const ROW_STATE_REASONS = ['COMPLETED', 'DUPLICATE', 'NOT_PLANNED', 'REOPENED'] as const satisfies ReadonlyArray<
  NonNullable<SubIssuesListItemNode['stateReason']>
>

/** An unrecognised state renders as open, matching the `closed` flag derived alongside it. */
export function toRowState(state: string | null | undefined): SubIssuesListItemNode['state'] {
  return state === 'CLOSED' ? 'CLOSED' : 'OPEN'
}

export function toRowStateReason(stateReason: string | null | undefined): SubIssuesListItemNode['stateReason'] {
  return ROW_STATE_REASONS.find(reason => reason === stateReason) ?? null
}

export type SubIssueOptimisticNodeInput = {
  id: string
  title: string
  /** "owner/name"; split into `repository.owner.login` / `repository.name`. */
  nameWithOwner?: string
  number?: number
  titleHTML?: string
  url?: string
  /** Open enums from the Relay-typed picker; narrowed to the row unions by the builder. */
  state?: string
  stateReason?: string | null
  databaseId?: SubIssuesListItemNode['databaseId']
}

export function buildSubIssueOptimisticNode(input: SubIssueOptimisticNodeInput): SubIssuesListItemNode {
  const [ownerLogin = '', repoName = ''] = (input.nameWithOwner ?? '/').split('/')
  const state = toRowState(input.state)
  return {
    id: input.id,
    closed: state === 'CLOSED',
    state,
    stateReason: toRowStateReason(input.stateReason),
    issueDependenciesSummary: {blockedBy: 0},
    assignees: {totalCount: 0, edges: []},
    url: input.url ?? '',
    repository: {name: repoName, owner: {login: ownerLogin}},
    databaseId: input.databaseId ?? null,
    number: input.number ?? 0,
    title: input.title,
    // Server `titleHTML` for a title is inline; render the temp title inline too so a title
    // like `# Heading` or `- item` can't inject block HTML into the list-item link.
    titleHTML: input.titleHTML ?? md2htmlInline(input.title),
    issueType: null,
    subIssuesSummary: {total: 0, completed: 0},
    closedByPullRequestsReferences: {totalCount: 0},
  }
}
